<template>
    <div class="nk-app-root">
        <key-search ref="keySearch"></key-search>
        <ai-assistant-widget></ai-assistant-widget>
        <pwa-install-fab></pwa-install-fab>
        <div class="nk-main ">
            <app-sidebar :is-dark-mode="isDarkMode"></app-sidebar>
            <div class="nk-wrap ">
                <div class="nk-header is-light nk-header-fixed sip-admin-header">
                    <div class="container-xl wide-xl">
                        <div class="nk-header-wrap sip-admin-header__wrap">
                            <div class="nk-header-brand d-flex align-items-center">
                                <div class="nk-menu-trigger sip-header-hamburger d-xl-none">
                                    <a class="nk-nav-toggle sip-icon-btn sip-header-btn--menu" id="OpenMenuSideBar" data-target="sidebarMenu" aria-label="Open menu">
                                        <admin-icon name="menu" :size="18"></admin-icon>
                                    </a>
                                </div>
                                <div class="sip-global-search-pill d-none d-md-flex" @click.prevent="openTypeAnywhereSearch" role="search" tabindex="0" @keydown.enter.prevent="openTypeAnywhereSearch" @keydown.space.prevent="openTypeAnywhereSearch">
                                    <admin-icon name="search" :size="16" class="sip-search-icon"></admin-icon>
                                    <span class="sip-search-placeholder">Search students, staff, classes, fees, documents...</span>
                                    <kbd class="sip-kbd">⌘K</kbd>
                                </div>
                            </div>
                            <div class="nk-header-tools">
                                <ul class="nk-quick-nav">
                                    <li class="nav-item d-md-none">
                                        <button
                                            type="button"
                                            class="sip-icon-btn sip-header-btn--search"
                                            :title="trans('general.any_search_title')"
                                            :aria-label="trans('general.any_search_title')"
                                            @click.prevent="openTypeAnywhereSearch"
                                        >
                                            <admin-icon name="search" :size="18"></admin-icon>
                                        </button>
                                    </li>
                                    <li class="nav-item dropdown" v-if="getAcademicSessions.length && hasPermission('change-academic-session')">
                                        <a class="sip-session-pill dropdown-toggle" href="#" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            <span class="sip-status-dot" aria-hidden="true"></span>
                                            <span class="sip-session-label">{{ sessionDisplayTitle }}</span>
                                            <admin-icon name="chevron-down" :size="12" class="sip-session-chevron"></admin-icon>
                                        </a>
                                        <div :class="['dropdown-menu dropdown-menu-md sip-session-dropdown', getConfig('direction') != 'rtl' ? 'dropdown-menu-right' : '']">
                                            <div class="sip-dropdown-header">
                                                <span class="sip-dropdown-header__title">Academic Sessions</span>
                                            </div>
                                            <ul class="dropdown-user sip-session-list">
                                                <li
                                                    v-for="academic_session in getAcademicSessions"
                                                    :key="academic_session.id"
                                                    :class="{'is-active': getDefaultAcademicSession && academic_session.id == getDefaultAcademicSession.id}"
                                                    @click="setDefaultAcademicSession(academic_session)"
                                                >
                                                    <span class="sip-session-item-name">{{ academic_session.name }}</span>
                                                    <admin-icon v-if="getDefaultAcademicSession && academic_session.id == getDefaultAcademicSession.id" name="check" :size="14" class="sip-check-icon text-primary"></admin-icon>
                                                </li>
                                                <li v-if="hasPermission('create-academic-session')" class="sip-add-session-btn" @click="$router.push('/academic/session')">
                                                    <admin-icon name="plus" :size="14" class="me-1"></admin-icon>
                                                    <span>{{ trans('academic.add_new_session') }}</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </li>
                                    <li class="nav-item d-none d-md-inline" v-if="getConfig('todo') && hasPermission('access-todo')">
                                        <router-link class="sip-icon-btn sip-header-btn--todo" to="/utility/todo" :title="trans('utility.todo')" :aria-label="trans('utility.todo')">
                                            <admin-icon name="check-circle" :size="18"></admin-icon>
                                        </router-link>
                                    </li>
                                    <li class="nav-item sip-theme-picker-slot">
                                        <theme-picker ref="themePicker"></theme-picker>
                                    </li>
                                    <li class="nav-item sip-inbox-slot">
                                        <header-notifications ref="headerInbox"></header-notifications>
                                    </li>
                                    <li class="nav-item d-none d-md-inline">
                                        <button
                                            type="button"
                                            class="sip-icon-btn sip-header-btn--theme"
                                            :title="isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
                                            :aria-label="isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
                                            @click.prevent="toggleDarkMode"
                                        >
                                            <admin-icon v-if="isDarkMode" name="sun" :size="18"></admin-icon>
                                            <admin-icon v-else name="moon" :size="18"></admin-icon>
                                        </button>
                                    </li>
                                    <li class="nav-item d-none d-md-inline">
                                        <button
                                            type="button"
                                            class="sip-icon-btn sip-header-btn--refresh"
                                            :class="{ 'is-refreshing': isRefreshing }"
                                            :title="trans('general.refresh')"
                                            :aria-label="trans('general.refresh')"
                                            :disabled="isRefreshing"
                                            @click.prevent="softRefresh"
                                        >
                                            <admin-icon name="refresh" :size="18"></admin-icon>
                                        </button>
                                    </li>
                                    <li class="dropdown user-dropdown">
                                        <a href="#" class="sip-profile-btn dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            <span class="sip-avatar">{{ userInitials }}</span>
                                            <div class="sip-profile-meta-wrap d-none d-md-flex flex-column text-start">
                                                <span class="sip-profile-name">{{ getAuthUser('name') }}</span>
                                                <span class="sip-profile-role-pill">{{ userRoleTitle }}</span>
                                            </div>
                                            <admin-icon name="chevron-down" :size="13" class="sip-profile-chevron"></admin-icon>
                                        </a>
                                        <div :class="['dropdown-menu dropdown-menu-md user-dropdown-menu sip-user-menu', getConfig('direction') != 'rtl' ? 'dropdown-menu-right' : '']">
                                            <div class="sip-user-menu-header">
                                                <div class="sip-user-menu-avatar">{{ userInitials }}</div>
                                                <div class="sip-user-menu-info">
                                                    <div class="sip-user-menu-name">{{ getAuthUser('name') }}</div>
                                                    <div class="sip-user-menu-email" v-if="getAuthUser('email')">{{ getAuthUser('email') }}</div>
                                                    <div class="sip-user-menu-badge">{{ userRoleTitle }}</div>
                                                </div>
                                            </div>
                                            <div class="dropdown-inner">
                                                <ul class="link-list link-list-opt no-bdr">
                                                    <li class="d-md-none">
                                                        <a href="#" @click.prevent="toggleDarkMode">
                                                            <admin-icon v-if="isDarkMode" name="sun" :size="16"></admin-icon>
                                                            <admin-icon v-else name="moon" :size="16"></admin-icon>
                                                            <span>{{ isDarkMode ? 'Light mode' : 'Dark mode' }}</span>
                                                        </a>
                                                    </li>
                                                    <li class="d-md-none">
                                                        <a href="#" @click.prevent="openThemePicker">
                                                            <admin-icon name="palette" :size="16"></admin-icon>
                                                            <span>Colour &amp; fonts</span>
                                                        </a>
                                                    </li>
                                                    <li class="d-md-none" v-if="getConfig('todo') && hasPermission('access-todo')">
                                                        <router-link to="/utility/todo">
                                                            <admin-icon name="check-circle" :size="16"></admin-icon>
                                                            <span>{{ trans('utility.todo') }}</span>
                                                        </router-link>
                                                    </li>
                                                    <li class="d-md-none">
                                                        <a href="#" :class="{ 'is-refreshing': isRefreshing }" @click.prevent="softRefresh">
                                                            <admin-icon name="refresh" :size="16"></admin-icon>
                                                            <span>{{ trans('general.refresh') }}</span>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a href="#" @click.prevent="openInbox">
                                                            <admin-icon name="bell" :size="16"></admin-icon>
                                                            <span>{{ trans('communication.notifications') }}</span>
                                                        </a>
                                                    </li>
                                                    <li v-if="!isPwaStandalone" data-sip-app-download="1">
                                                        <a href="#" @click.prevent="installPwaApp">
                                                            <admin-icon name="download" :size="16"></admin-icon>
                                                            <span>App Download</span>
                                                        </a>
                                                    </li>
                                                    <li v-if="hasRole('admin')"><a href="/subscription"><admin-icon name="card" :size="16"></admin-icon><span>{{trans('subscription.subscription')}}</span></a></li>
                                                    <li><a href="/change/password"><admin-icon name="lock" :size="16"></admin-icon><span>{{trans('user.change_password')}}</span></a></li>
                                                    <li class="border-top pt-1 mt-1"><a href="#" class="text-danger" @click.prevent="logout"><admin-icon name="logout" :size="16"></admin-icon><span>{{trans('auth.logout')}}</span></a></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div><!-- .nk-header-tools -->
                        </div><!-- .nk-header-wrap -->
                    </div><!-- .container-xl -->
                </div>

                <!---<app-header></app-header>-->
                <!-- <div v-html="message" v-if="!getConfig('mode')"></div> -->

                <div class="nk-content nk-content-fluid">
                    <div class="container-xl wide-xl">
                        <div class="nk-content-body sip-content-outlet" :aria-busy="showRouteSkeleton ? 'true' : 'false'">
                            <transition name="sip-page">
                                <router-view :key="pageViewKey"></router-view>
                            </transition>
                        </div>
                    </div>
                </div>
                <div class="nk-footer">
                    <div class="container-xl wide-xl">
                        <div class="nk-footer-wrap">
                            <app-footer></app-footer>
                        </div>
                    </div>
                </div>
                <mobile-bottom-nav></mobile-bottom-nav>
            </div>
        </div>
    </div>
</template>

<script>
import AppHeader from './partials/header.vue'
import AppSidebar from './partials/sidebar.vue'
import AppFooter from './partials/footer.vue'
import globalSearch from './partials/global-search'
import KeySearch from '@components/key-search.vue'
import AiAssistantWidget from '@components/ai-assistant-widget.vue'
import PwaInstallFab from '@components/pwa-install-fab.vue'
import ThemePicker from '@components/theme-picker.vue'
import MobileBottomNav from '@components/mobile-bottom-nav.vue'
import HeaderNotifications from '@components/header-notifications.vue'
import { applySchoolAppearance } from '../themes/school-themes'
import { navLoading, startNavProgress, stopNavProgress } from '../services/fast-loading'

export default {
    data() {
        return {
            message: '',
            sidebar: helper.getConfig('user_sidebar') || helper.getConfig('sidebar'),
            isDarkMode: document.body.classList.contains('dark-mode'),
            contentEpoch: 0,
            refreshRequested: false,
            isMobileSidebarActive: false
        }
    },
    components: {
        AppHeader, AppSidebar, AppFooter, KeySearch, AiAssistantWidget, PwaInstallFab, ThemePicker, MobileBottomNav, HeaderNotifications, globalSearch
    },
    mounted() {
        if (window.sipHideSplash) window.sipHideSplash();
        const preloader = document.querySelector('.preloader, .js-preloader');
        if (preloader) preloader.style.display = 'none';
        helper.notification();

        // Restore dark mode preference
        try {
            const saved = localStorage.getItem('dark-mode');
            if (saved === '1') {
                document.body.classList.add('dark-mode');
                document.documentElement.classList.add('dark-mode');
                this.isDarkMode = true;
            }
        } catch (e) {}
        applySchoolAppearance(null, true);

        /*$('.scroll-sidebar').slimScroll({
            position: 'left',
            size: "5px",
            height: '100%',
            color: '#dcdcdc'
        });
        $('.message-scroll').slimScroll({
            position: 'right',
            size: "5px",
            height: '570',
            color: '#dcdcdc'
        });
        $('.slimscrollright').slimScroll({
            position: 'left',
            size: "5px",
            height: '100%',
            color: '#dcdcdc'
        });*/

        $("body").addClass("fix-header fix-sidebar");

        if (this.sidebar == 'mini')
            //this.miniSidebar();

            $('#theme').attr('href', '/css/colors/' + (helper.getConfig('user_color_theme') || helper.getConfig('color_theme')) + '.css');

        var set = function () {
            var width = (window.innerWidth > 0) ? window.innerWidth : this.screen.width;
            var topOffset = 70;
            if (width < 1170) {
                //$("body").addClass("mini-sidebar");
                $('.navbar-brand span').hide();
                $(".scroll-sidebar, .slimScrollDiv").css("overflow-x", "visible").parent().css("overflow", "visible");
                $(".sidebartoggler i").addClass("fa-arrow-circle-right");
                $(".sidebartoggler i").removeClass("fa-arrow-circle-left");
            }

            var height = ((window.innerHeight > 0) ? window.innerHeight : this.screen.height) - 1;
            height = height - topOffset;
            if (height < 1) height = 1;
            if (height > topOffset) {
                $(".page-wrapper").css("min-height", (height) + "px");
            }

        };
        $(window).ready(set);
        $(window).on("resize", set);

        $(document).on('click', ".right-sidebar-toggle", function () {
            $(".right-sidebar").slideDown(50);
            $(".right-sidebar").toggleClass("shw-rside");
        });

        $(document).on('click', '.sidebartoggler', function () {
            if ($("body").hasClass("mini-sidebar")) {
                $("body").trigger("resize");
                $(".scroll-sidebar, .slimScrollDiv").css("overflow", "hidden").parent().css("overflow", "visible");
                $("body").removeClass("mini-sidebar");
                $('.navbar-brand span').show();
                $(".sidebartoggler i").removeClass("fa-arrow-circle-right");
                $(".sidebartoggler i").addClass("fa-arrow-circle-left");
            }
            else {
                $("body").trigger("resize");
                $(".scroll-sidebar, .slimScrollDiv").css("overflow-x", "visible").parent().css("overflow", "visible");
                $("body").addClass("mini-sidebar");
                $('.navbar-brand span').hide();
                $(".sidebartoggler i").removeClass("fa-arrow-circle-left");
                $(".sidebartoggler i").addClass("fa-arrow-circle-right");
            }
        });

        //$(".fix-header .topbar").stick_in_parent();

        $(document).on('click', ".nav-toggler", function () {
            $("body").toggleClass("show-sidebar");
            $(".nav-toggler i").toggleClass("fa-bars");
            $(".nav-toggler i").toggleClass("fa-times");
        });

        const syncMobileSidebar = () => {
            const sidebar = document.getElementById('MainSideBarRightMenu');
            const mobile = typeof window !== 'undefined' && window.innerWidth < 992;
            const active = !!(mobile && sidebar && sidebar.classList.contains('nk-sidebar-active'));
            this.isMobileSidebarActive = active;
            document.body.classList.toggle('nav-shown', active);
            document.body.classList.toggle('sip-mobile-sidebar-open', active);
            this.syncBodySidebarOverlay(active);
        };

        this._syncMobileSidebar = syncMobileSidebar;
        this.ensureBodySidebarOverlay();
        syncMobileSidebar();

        const sidebarEl = document.getElementById('MainSideBarRightMenu');
        if (sidebarEl && typeof MutationObserver !== 'undefined') {
            this._sidebarObserver = new MutationObserver(syncMobileSidebar);
            this._sidebarObserver.observe(sidebarEl, { attributes: true, attributeFilter: ['class'] });
        }

        this._onSidebarOpened = () => {
            this.isMobileSidebarActive = window.innerWidth < 992;
            if (this.isMobileSidebarActive) {
                document.body.classList.add('nav-shown', 'sip-mobile-sidebar-open');
            }
            this.syncBodySidebarOverlay(this.isMobileSidebarActive);
        };
        this._onSidebarClosed = () => {
            this.isMobileSidebarActive = false;
            document.body.classList.remove('nav-shown', 'sip-mobile-sidebar-open');
            this.syncBodySidebarOverlay(false);
        };
        window.addEventListener('sip-sidebar-closed', this._onSidebarClosed);
        window.addEventListener('sip-sidebar-opened', this._onSidebarOpened);

        $(document).on('click', "#OpenMenuSideBar", (e) => {
            e.preventDefault();
            const sidebar = $("#MainSideBarRightMenu");
            sidebar.toggleClass("nk-sidebar-active");
            syncMobileSidebar();
            if (sidebar.hasClass("nk-sidebar-active")) {
                window.dispatchEvent(new CustomEvent('sip-sidebar-opened'));
            } else {
                window.dispatchEvent(new CustomEvent('sip-sidebar-closed'));
            }
        });

        $(document).on('click', "#HideRightSideBarMenu", (e) => {
            e.preventDefault();
            this.closeMobileSidebar();
        });

        $(document).on('click', ".nk-sidebar-overlay", () => {
            this.closeMobileSidebar();
        });

        this._onEscapeKey = (e) => {
            if (e.key === 'Escape' && this.isMobileSidebarActive) {
                this.closeMobileSidebar();
            }
        };
        document.addEventListener('keydown', this._onEscapeKey);

        this._onDocClickCloseSidebar = (e) => {
            if (!this.isMobileSidebarActive || window.innerWidth >= 992) return;
            const sidebar = document.getElementById('MainSideBarRightMenu');
            if (sidebar && sidebar.contains(e.target)) return;
            if (e.target && e.target.closest && e.target.closest('#OpenMenuSideBar, #HideRightSideBarMenu, .sip-mobile-nav-item--btn, .nk-nav-toggle')) {
                return;
            }
            this.closeMobileSidebar();
        };
        document.addEventListener('click', this._onDocClickCloseSidebar);

        this._onOverlayViewport = () => {
            if (window.innerWidth >= 992 && this.isMobileSidebarActive) {
                this.closeMobileSidebar();
            } else {
                syncMobileSidebar();
            }
        };
        window.addEventListener('resize', this._onOverlayViewport);
        
        //$('#sidebarnav').metisMenu();
    },
    methods: {
        miniSidebar() {
            $('body').addClass("mini-sidebar");
            $("body").trigger("resize");
            $('.navbar-brand span').hide();
            $(".scroll-sidebar, .slimScrollDiv").css("overflow-x", "visible").parent().css("overflow", "visible");
            $(".sidebartoggler i").removeClass("fa-arrow-circle-left");
            $(".sidebartoggler i").addClass("fa-arrow-circle-right");
        },
        normalSidebar() {
            $("body").trigger("resize");
            $('.navbar-brand span').show();
            $(".scroll-sidebar, .slimScrollDiv").css("overflow", "hidden").parent().css("overflow", "visible");
            $('body').removeClass("mini-sidebar");
            $(".sidebartoggler i").removeClass("fa-arrow-circle-right");
            $(".sidebartoggler i").addClass("fa-arrow-circle-left");
        },
        getConfig(config) {
            return helper.getConfig(config);
        },
        openTypeAnywhereSearch() {
            const search = this.$refs.keySearch;
            if (search && typeof search.open === 'function') {
                search.open();
            }
        },
        toggleDarkMode() {
            document.body.classList.toggle('dark-mode');
            this.isDarkMode = document.body.classList.contains('dark-mode');
            document.documentElement.classList.toggle('dark-mode', this.isDarkMode);
            try {
                localStorage.setItem('dark-mode', this.isDarkMode ? '1' : '0');
            } catch (e) {}
            applySchoolAppearance(null, true);
        },
        openThemePicker() {
            window.setTimeout(() => {
                const picker = this.$refs.themePicker;
                if (!picker) return;
                if (typeof picker.openPanel === 'function') {
                    picker.openPanel();
                } else {
                    picker.open = true;
                }
            }, 50);
        },
        openInbox() {
            if (window.$) {
                window.$('.user-dropdown .dropdown-menu').removeClass('show');
                window.$('.user-dropdown').removeClass('show');
            }
            this.$nextTick(() => {
                const inbox = this.$refs.headerInbox;
                if (inbox && typeof inbox.openPanel === 'function') {
                    inbox.openPanel();
                }
            });
        },
        installPwaApp() {
            if (window.$) {
                window.$('.user-dropdown .dropdown-menu').removeClass('show');
                window.$('.user-dropdown').removeClass('show');
            }
            if (window.SipinorPwa && typeof window.SipinorPwa.install === 'function') {
                window.SipinorPwa.install();
                return;
            }
            window.dispatchEvent(new Event('sipinor-pwa-install'));
        },
        softRefresh(){
            if (this.refreshRequested) {
                return;
            }
            this.refreshRequested = true;
            startNavProgress({ route: true });
            axios.get('/api/chache-clear')
                .then(() => {
                    helper.showSuccessMsg('Cache cleared successfully');
                })
                .catch(error => {
                    helper.showErrorMsg(error);
                });
            this.contentEpoch += 1;
            this.$nextTick(() => {
                stopNavProgress();
            });
        },
        chache(){
            this.softRefresh();
        },
        logout(){
            helper.logout().then(() => {
                this.$router.push('/login')
            })
        },
        getAuthUser(name){
            return helper.getAuthUser(name);
        },
        getConfig(name){
            return helper.getConfig(name);
        },
        hasPermission(permission){
            return helper.hasPermission(permission);
        },
        hasRole(role){
            return helper.hasRole(role);
        },
        setDefaultAcademicSession(academic_session){
            axios.post('/api/academic/session/'+academic_session.id+'/user/default')
                .then(response => {
                    this.$store.dispatch('setDefaultAcademicSession',academic_session);
                    startNavProgress({ route: true });
                    location.reload();
                })
                .catch(error => {
                    helper.showErrorMsg(error);
                });
        },
        ensureBodySidebarOverlay() {
            if (this._sidebarOverlay && this._sidebarOverlay.isConnected) {
                return this._sidebarOverlay;
            }
            const el = document.createElement('div');
            el.className = 'nk-sidebar-overlay sip-sidebar-scrim';
            el.setAttribute('aria-hidden', 'true');
            el.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeMobileSidebar();
            });
            document.body.appendChild(el);
            this._sidebarOverlay = el;
            return el;
        },
        syncBodySidebarOverlay(active) {
            const el = this.ensureBodySidebarOverlay();
            const show = !!active && window.innerWidth < 992;
            el.classList.toggle('is-visible', show);
            el.setAttribute('aria-hidden', show ? 'false' : 'true');
        },
        closeMobileSidebar() {
            const sidebar = document.getElementById('MainSideBarRightMenu');
            if (sidebar && sidebar.classList.contains('nk-sidebar-active')) {
                sidebar.classList.remove('nk-sidebar-active');
            }
            document.body.classList.remove('nav-shown', 'sip-mobile-sidebar-open');
            this.isMobileSidebarActive = false;
            this.syncBodySidebarOverlay(false);
            window.dispatchEvent(new CustomEvent('sip-sidebar-closed'));
        }
    },
    beforeUnmount() {
        if (this._sidebarObserver) {
            this._sidebarObserver.disconnect();
        }
        if (this._onEscapeKey) {
            document.removeEventListener('keydown', this._onEscapeKey);
        }
        if (this._onDocClickCloseSidebar) {
            document.removeEventListener('click', this._onDocClickCloseSidebar);
        }
        if (this._onOverlayViewport) {
            window.removeEventListener('resize', this._onOverlayViewport);
        }
        if (this._onSidebarOpened) {
            window.removeEventListener('sip-sidebar-opened', this._onSidebarOpened);
        }
        if (this._onSidebarClosed) {
            window.removeEventListener('sip-sidebar-closed', this._onSidebarClosed);
        }
        if (this._sidebarOverlay && this._sidebarOverlay.parentNode) {
            this._sidebarOverlay.parentNode.removeChild(this._sidebarOverlay);
            this._sidebarOverlay = null;
        }
    },
    computed: {
        getLogo(){
            return helper.getLogo();
        },
        getAcademicSessions(){
            return helper.getAcademicSessions();
        },
        getDefaultAcademicSession(){
            return helper.getDefaultAcademicSession();
        },
        getAuthUserRoles() {
            return helper.ucword(this.$store.getters.getAuthUserRoles);
        },
        userRoleTitle() {
            const roles = this.getAuthUserRoles;
            if (roles && typeof roles === 'string' && roles.trim()) return roles.trim();
            if (this.hasRole('admin')) return 'Administrator';
            return 'School Admin';
        },
        sessionDisplayTitle() {
            if (!this.getDefaultAcademicSession) return this.trans('academic_session.choose_session') || 'Choose Session';
            const name = (this.getDefaultAcademicSession.name || '').trim();
            if (/^session\b/i.test(name)) return name;
            return `Session ${name}`;
        },
        userInitials() {
            const name = (this.getAuthUser('name') || '').trim();
            if (!name) return 'A';
            const parts = name.split(/\s+/).filter(Boolean);
            const letters = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
            return letters.join('') || 'A';
        },
        isPwaStandalone() {
            if (window.SipinorPwa && typeof window.SipinorPwa.isStandalone === 'function') {
                return window.SipinorPwa.isStandalone();
            }
            if (window.navigator && window.navigator.standalone === true) return true;
            return !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
        },
        pageViewKey() {
            return this.contentEpoch + ':' + this.$route.fullPath;
        },
        showRouteSkeleton() {
            return navLoading.route;
        },
        isRefreshing() {
            return this.refreshRequested && (navLoading.route || navLoading.bar);
        },
    },
    watch: {
        sidebar(val) {
            if (val == 'mini')
                this.miniSidebar();
            else
                this.normalSidebar();

        },
        showRouteSkeleton(val) {
            if (!val) {
                this.refreshRequested = false;
            }
        },
        $route() {
            this.closeMobileSidebar();
        }
    }
}
</script>

<style>
html body.fix-sidebar .nk-wrap:not(.nk-wrap-nosidebar),
html body.fix-sidebar .nk-sidebar + .nk-wrap {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    margin-top: 0 !important;
    background: var(--sip-bg) !important;
    background-color: var(--sip-bg) !important;
    background-image: none !important;
}

@media (max-width: 767.98px) {
    html body.fix-sidebar .nk-wrap:not(.nk-wrap-nosidebar),
    html body.fix-sidebar .nk-sidebar + .nk-wrap {
        padding-bottom: var(--sip-mobile-bottom-inset, calc(var(--sip-mobile-bottom-nav-h, 60px) + env(safe-area-inset-bottom, 0px))) !important;
    }
}

html body .nk-app-root .nk-footer,
html body .nk-app-root .nk-wrap > .nk-footer {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    box-shadow: none !important;
}

html body .nk-app-root .nk-footer .nk-footer-wrap,
html body .nk-app-root .nk-footer .container-xl,
html body .nk-app-root .nk-footer .wide-xl {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
}

@media (min-width: 992px) {
    html body.fix-sidebar .nk-sidebar + .nk-wrap,
    html body.fix-sidebar .nk-sidebar-overlay + .nk-wrap {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        padding-left: var(--sidebar-width, 260px) !important;
        margin-top: 0 !important;
    }

    html body.fix-sidebar .nk-sidebar + .nk-wrap > .nk-header-fixed,
    html body .nk-header.nk-header-fixed.sip-admin-header {
        position: sticky !important;
        top: 0 !important;
        left: auto !important;
        right: auto !important;
        margin-top: 0 !important;
        width: 100% !important;
    }
}

@media (min-width: 1200px) {
    html body.fix-sidebar .nk-wrap:not(.nk-wrap-nosidebar),
    html body.fix-sidebar .nk-sidebar + .nk-wrap {
        padding: 0 0 0 var(--sidebar-width, 260px) !important;
    }
}
</style>
